import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import * as d3 from "d3";

import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import ActionsContext from "common/ActionsContext";
import BqnContext from "common/BqnContext";
import NVLineChart from "common/graphs/NVLineChart";
import Request from "common/Request";
import { retrieveRange, getSampleSize } from "common/api";
import RangeSliderInput from "common/RangeSliderInput";
import { useScreenSize } from "common/hooks";
import {
  PERIOD_TO_HOURS,
  PERIOD_TO_LINES,
  altPeriodScopeChoices,
} from "common/constants";
import { getBqnIdCmdPart } from "common/api";
import { parseStatsRtxData} from "./api";
import { getInRangeItems } from "../utils";
import useDebouncer from "common/hooks/debouncer";


const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

const SelectWrapper = styled.div`
  width: 150px;
`;

const ChartContainer = styled.div`
  display: flex;
  min-height: 500px;
  margin-top: 20px;

  svg.nvd3-svg .nv-axis path.domain {
    stroke-opacity: 0;
  }

  .nv-y.nv-axis .nv-axisMaxMin {
    display: none;
  }

  .nv-y.nv-axis .tick.zero line {
    stroke: transparent !important;
  }

  .nvd3 .nv-groups path.nv-line {
    stroke-width: 1.1;
  }
`;

const RangeContainer = styled.div`
  margin: 20px 0;
`;

export const useStatsRtxData = (
  period = 'Day',
  firstRequest = null
) => {
  const actions = useContext(ActionsContext);
  const [request, setRequest] = useState(firstRequest);
  const [initialRange, setInitialRange] = useState();
  const [range, setRange] = useState();
  const debounce = useDebouncer(500 /*ms*/);
  const {
    bqnId,
    clusterStatus: { mode },
  } = useContext(BqnContext);
  const doRequest = (period) => {
    setRange(undefined);
    const hours = PERIOD_TO_HOURS[period];
    return retrieveRange(hours).then(({ from, to }) => {
      setInitialRange({ from, to });
      return ifCl
        .run(`show statistics retransmission lines ${PERIOD_TO_LINES[period]}${getBqnIdCmdPart(mode, bqnId)}`)
        .then((response) => {
          return parseStatsRtxData(response, period, from, to);
        });
    });
  };

  const doLoad = () => {
    setRequest(doRequest(period));
  };

  useEffect(() => {
    debounce(() => doLoad());
  }, [period, bqnId]);

  useEffect(() => actions.recv('do-load', doLoad), [doLoad]);
  return { request, doLoad, initialRange, range, setRange };
};

const StatsRtxReport = () => {
  const [period, setPeriod] = useState("Day");
  const { width: windowWidth } = useScreenSize();
  const { request, initialRange, range, setRange } = useStatsRtxData(period, null);
  const POINTS_PER_TILE_CHART = windowWidth / 2;
  return (
    <Wrapper>
      <ArrangeInColumns rowGap="0">
        <SelectWrapper>
          <SelectInputWithIcon
            title="Date Range"
            name="Date Range"
            icon="date_range"
            selected={period}
            onChange={({ target }) => setPeriod(target.value)}
            options={altPeriodScopeChoices}
          />
        </SelectWrapper>
      </ArrangeInColumns>
      <ChartContainer>
        <Request during={request}>
          {({ items, fields }) => {
            const selectedRange = range || initialRange;
            const sampleSize = getSampleSize(selectedRange, POINTS_PER_TILE_CHART, period);
            const inRangeItems = getInRangeItems(items, selectedRange, fields, sampleSize);
            
            return (
              <NVLineChart
                xField="time"
                items={inRangeItems}
                yAxisUnits={"%"}
                yAxisFormat={d3.format(".1f")}
                xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
                fields={["Rtx%-Access"].map((field) => {
                  return { name: field, label: field };
                })}
                yAxisTopGap={0.05}
                yAxisTicksNumber={8}
                xAxisTicksNumber={8}
                yTooltipFormat={(d) => d3.format(".2f")(d) + " %"}
                fixedYRange={[0, 1]}
              />
            );
          }}
        </Request>
      </ChartContainer>
      <RangeContainer>
        {initialRange && (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        )}
      </RangeContainer>
    </Wrapper>
  );
};

export default StatsRtxReport;
